import { CompanyStatus } from "@pentacode/core";
import { css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Checkbox } from "../checkbox";
import { Dialog } from "../dialog";

export interface YnaEnableResult {
    confirmed: boolean;
    notifyOwner: boolean;
    notifyManagers: boolean;
}

export type YnaEnableCompany = {
    name: string;
    effectiveCompanyStatus: CompanyStatus;
};

@customElement("ptc-yna-enable-dialog")
export class YnaEnableDialog extends Dialog<YnaEnableCompany, YnaEnableResult> {
    @state()
    private _notifyOwner = false;

    @state()
    private _notifyManagers = false;

    @state()
    private _companyActive = false;

    static styles = [
        ...Dialog.styles,
        Checkbox.styles,
        css`
            .checkbox-container {
                padding-bottom: 1em;
            }
        `,
    ];

    renderContent() {
        const { name } = this.input;
        if (!name) {
            return html``;
        }

        return html`
            <div class="padded-medium spacing vertical layout">
                <div class="large text-centering">Yna aktivieren</div>

                <p class="text-centering">Soll Yna für "<strong>${name}</strong>" wirklich aktiviert werden?</p>

                <div class="double-margined checkbox-container">
                    <label>E-Mail Benachrichtigungen versenden an: </label>
                    <div class="spacing vertical layout">
                        <ptc-checkbox-button
                            label="Besitzer"
                            buttonClass="slim ghost"
                            .checked=${this._notifyOwner}
                            @change=${(e: Event) => (this._notifyOwner = (e.target as Checkbox).checked)}
                            ?disabled=${!this._companyActive}
                        ></ptc-checkbox-button>
                        <ptc-checkbox-button
                            label="Manager"
                            buttonClass="slim ghost"
                            .checked=${this._notifyManagers}
                            @change=${(e: Event) => (this._notifyManagers = (e.target as Checkbox).checked)}
                            ?disabled=${!this._companyActive}
                        ></ptc-checkbox-button>
                        ${(this._notifyOwner || this._notifyManagers) && this._companyActive
                            ? html`
                                  <p class="text-centering subtle small">
                                      Beim Speichern werden E-Mail Benachrichtigungen über die Aktivierung von Yna an
                                      die Besitzer und Manager des Unternehmens versendet.
                                  </p>
                              `
                            : null}
                        ${!this._companyActive
                            ? html`
                                  <p class="text-centering red colored-text small">
                                      Das Unternehmen ist weder aktiv noch in Testphase. E-Mail Benachrichtigungen
                                      werden nicht versendet.
                                  </p>
                              `
                            : null}
                    </div>
                </div>

                <div class="horizontal spacing evenly stretching layout">
                    <button class="primary" @click=${this._confirm}>Speichern</button>
                    <button @click=${this._cancel}>Abbrechen</button>
                </div>
            </div>
        `;
    }

    async show(input: YnaEnableCompany) {
        this._notifyOwner = false;
        this._notifyManagers = false;
        this._companyActive = false;

        if (
            [CompanyStatus.TrialWithoutContract, CompanyStatus.Trialing, CompanyStatus.Active].some(
                (s) => s === input.effectiveCompanyStatus
            )
        ) {
            this._companyActive = true;
            this._notifyOwner = true;
            this._notifyManagers = true;
        }
        return super.show(input);
    }

    private _confirm() {
        this.done({
            confirmed: true,
            notifyOwner: this._notifyOwner,
            notifyManagers: this._notifyManagers,
        });
    }

    private _cancel() {
        this.done({
            confirmed: false,
            notifyOwner: false,
            notifyManagers: false,
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-yna-enable-dialog": YnaEnableDialog;
    }
}
